import React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  useAiService,
  useAddAiServiceDomain,
  useToggleAiService,
  useDeleteAiService,
  useAddFileUploadDomain,
  useRemoveFileUploadDomain,
  formatDate,
} from '@redbax/shared';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  DataTable,
  LoadingSkeleton,
  Separator,
  type Column,
} from '@redbax/ui-kit';
import { ArrowLeft, Plus, Trash2, Globe, Upload } from 'lucide-react';
import type { AiServiceDomain, FileUploadDomain } from '@redbax/shared';

function categoryLabel(category: string): string {
  switch (category) {
    case 'CodingAssistant':
      return 'Coding Assistant';
    case 'ImageMedia':
      return 'Image & Media';
    case 'ChineseAI':
      return 'Chinese AI';
    default:
      return category;
  }
}

const domainColumns: Column<AiServiceDomain>[] = [
  {
    key: 'domain',
    header: 'Domain',
    sortable: true,
    render: (row) => (
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-text-secondary" />
        <span className="font-mono text-sm">{row.domain}</span>
      </div>
    ),
  },
  {
    key: 'createdAt',
    header: 'Added',
    render: (row) => (
      <span className="text-sm text-text-secondary">
        {formatDate(row.createdAt)}
      </span>
    ),
  },
];

const fileUploadDomainColumns = (
  onRemove: (domainId: string) => void,
): Column<FileUploadDomain>[] => [
  {
    key: 'domainPattern',
    header: 'Domain Pattern',
    sortable: true,
    render: (row) => (
      <div className="flex items-center gap-2">
        <Upload className="h-4 w-4 text-text-secondary" />
        <span className="font-mono text-sm">{row.domainPattern}</span>
      </div>
    ),
  },
  {
    key: 'createdAt',
    header: 'Added',
    render: (row) => (
      <span className="text-sm text-text-secondary">
        {formatDate(row.createdAt)}
      </span>
    ),
  },
  {
    key: 'actions',
    header: '',
    render: (row) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(row.id);
        }}
        aria-label={`Remove ${row.domainPattern}`}
      >
        <Trash2 className="h-4 w-4 text-text-secondary hover:text-red-accent" />
      </Button>
    ),
  },
];

export function AiServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { data: service, isLoading } = useAiService(serviceId ?? '');
  const addDomain = useAddAiServiceDomain(serviceId ?? '');
  const toggleService = useToggleAiService();
  const deleteService = useDeleteAiService();
  const addFileUploadDomain = useAddFileUploadDomain(serviceId ?? '');
  const removeFileUploadDomain = useRemoveFileUploadDomain(serviceId ?? '');

  const [newDomain, setNewDomain] = React.useState('');
  const [newUploadPattern, setNewUploadPattern] = React.useState('');

  const handleAddDomain = () => {
    if (!newDomain.trim()) return;
    addDomain.mutate({ domain: newDomain.trim() });
    setNewDomain('');
  };

  const handleAddUploadDomain = () => {
    if (!newUploadPattern.trim()) return;
    addFileUploadDomain.mutate({ domainPattern: newUploadPattern.trim() });
    setNewUploadPattern('');
  };

  const handleRemoveUploadDomain = (domainId: string) => {
    removeFileUploadDomain.mutate(domainId);
  };

  const handleDelete = () => {
    if (!service || service.isBuiltIn) return;
    deleteService.mutate(service.id, {
      onSuccess: () => navigate('/ai-services'),
    });
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="AI Service" />
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (!service) {
    return (
      <div>
        <PageHeader title="AI service not found" />
      </div>
    );
  }

  const uploadColumns = fileUploadDomainColumns(handleRemoveUploadDomain);

  return (
    <div>
      <PageHeader
        title={service.name}
        description={service.provider ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/ai-services')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <button
              onClick={() => toggleService.mutate(service.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                service.isEnabled
                  ? 'bg-green-ok/20 text-green-ok hover:bg-green-ok/30'
                  : 'bg-surface-raised text-text-secondary hover:bg-surface-raised/80'
              }`}
            >
              {service.isEnabled ? 'Enabled' : 'Disabled'}
            </button>
            {!service.isBuiltIn && (
              <Button variant="secondary" onClick={handleDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <Badge variant="outline">{categoryLabel(service.category)}</Badge>
        {service.isBuiltIn && <Badge variant="secondary">Built-in</Badge>}
        {service.versionId && <Badge variant="outline">v{service.versionId}</Badge>}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monitored Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={domainColumns}
              data={service.domains}
              keyExtractor={(d) => d.id}
              emptyMessage="No domains configured"
            />

            {!service.isBuiltIn && (
              <>
                <Separator className="my-4" />
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label>Add Domain</Label>
                    <Input
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="e.g., api.example.com"
                      className="mt-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddDomain();
                      }}
                    />
                  </div>
                  <Button onClick={handleAddDomain} disabled={!newDomain.trim()}>
                    <Plus className="mr-1 h-4 w-4" />
                    Add
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">File Upload Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-text-secondary">
              Additional domains where file uploads are intercepted for DLP scanning.
              Use wildcard patterns like{' '}
              <code className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-xs">
                *.oaiusercontent.com
              </code>{' '}
              to match regional subdomains.
            </p>

            <DataTable
              columns={uploadColumns}
              data={service.fileUploadDomains ?? []}
              keyExtractor={(d) => d.id}
              emptyMessage="No file upload domains configured"
            />

            <Separator className="my-4" />
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label>Add Upload Domain Pattern</Label>
                <Input
                  value={newUploadPattern}
                  onChange={(e) => setNewUploadPattern(e.target.value)}
                  placeholder="e.g., *.oaiusercontent.com"
                  className="mt-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddUploadDomain();
                  }}
                />
              </div>
              <Button
                onClick={handleAddUploadDomain}
                disabled={!newUploadPattern.trim() || addFileUploadDomain.isPending}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
